// What an agent share was asked, for its owner (byj-1wv.48): how many
// questions, from how many people, at what cost, and how those who asked
// rated the answers — folded to a line in the share's side, and opened to
// the latest exchanges in a drawer, their words where the share kept its
// callers' questions. An agent made with the builder turns one into an
// example from its page (AgentPage); this is where the owner sees them.
import { useEffect, useState, type ReactNode } from "react";
import { api, type AgentExchangeView, type AgentUse } from "../api";
import { money, t, whenMinute } from "../i18n";
import { Drawer } from "./Drawer";
import { FoldPanel } from "./SidePanel";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export type AgentUseApi = Pick<typeof api, "agentUse">;

/** A rating as a sign, or nothing. */
export const ratingSign = (r: number) => (r === 1 ? "👍" : r === -1 ? "👎" : "");

/** The exchanges, newest first: who asked and when, its cost and rating, and its words where kept; `action` adds one of the owner's to each kept. */
export function ExchangeList({ exchanges, action }: { exchanges: AgentExchangeView[]; action?: (x: AgentExchangeView) => ReactNode }) {
  return (
    <ul className="exchange-list">
      {exchanges.map((x) => (
        <li key={x.seq} className="exchange" data-testid="exchange">
          <div className="exchange-head small">
            <span className="exchange-who">{x.asker_name || x.asker_id}</span>
            <span className="muted">{[whenMinute(x.at), x.cost_usd > 0 ? money(x.cost_usd) : null].filter(Boolean).join(" · ")}</span>
            {x.rating !== 0 && (
              <span className="exchange-rating" aria-label={x.rating === 1 ? t("session:rateUp") : t("session:rateDown")}>
                {ratingSign(x.rating)}
              </span>
            )}
          </div>
          {x.question ? (
            <>
              <p className="exchange-question">{x.question}</p>
              {x.answer && (
                <details className="exchange-answer small">
                  <summary>{t("session:agentUseAnswer")}</summary>
                  <p>{x.answer}</p>
                </details>
              )}
              {action?.(x)}
            </>
          ) : (
            <p className="muted small">{t("session:agentUseNotKept")}</p>
          )}
        </li>
      ))}
    </ul>
  );
}

/** The owner's summary of an agent share's use; `turns` read again as the share's turns grow. */
export function AgentUsePanel({ shareId, turns, client = api }: { shareId: string; turns: number; client?: AgentUseApi }) {
  const [use, setUse] = useState<AgentUse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    client.agentUse(shareId).then(
      (u) => live && (setUse(u), setError(null)),
      (err) => live && setError(errText(err)),
    );
    return () => {
      live = false;
    };
  }, [client, shareId, turns]);
  const line = use
    ? t("session:agentUseLine", { questions: use.questions, askers: use.askers, cost: money(use.cost_usd) })
    : error
      ? t("common:error", { m: error })
      : t("common:loading");
  return (
    <FoldPanel label={t("session:agentUse")} testid="agent-use" summary={<span className="muted small">{line}</span>}>
      {use && (
        <>
          <p className="small">{t("session:agentUseRated", { up: use.up, down: use.down })}</p>
          <p className="muted small">{use.keeping ? t("session:agentUseKeeping", { kept: use.kept }) : t("session:agentUseNotKeeping", { kept: use.kept })}</p>
          {use.exchanges.length > 0 && (
            <button className="small" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? "agent-use-drawer" : undefined} onClick={() => setOpen(true)}>
              {t("session:agentUseSee")}
            </button>
          )}
          <Drawer id="agent-use-drawer" title={t("session:agentUse")} open={open} onClose={() => setOpen(false)}>
            <ExchangeList exchanges={use.exchanges} />
          </Drawer>
        </>
      )}
    </FoldPanel>
  );
}
